import {
  countLabel,
  panelAction,
  panelActionCss,
  positive,
  warningChip,
  warningChipCss,
  warningChipIn,
} from '../armKit';
import { bookmarkCounts, mentionCounts } from '../batchFetchers';
import { analyticsDocumentUrl, upgradeUrl, workEditUrl } from '../destinations';
import { viewedUser, viewerIsPremium } from '../profileFacts';
import {
  cardPartCss,
  chipsRowCss,
  compactCardsCss,
  mediaCss,
} from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { ProfileCard } from '../surface';

const FLAG = 'corpusinventory';
const EDIT_SOURCE = 'profile-liautotest-inventory-edit';
const DOWNLOAD_SOURCE = 'profile-liautotest-inventory-download';
const IMPACT_SOURCE = 'profile-liautotest-inventory-impact';
const BOOKMARKS_SOURCE = 'profile-liautotest-inventory-bookmarks';
const MENTIONS_SOURCE = 'profile-liautotest-inventory-mentions';
const NO_FILE_SOURCE = 'profile-liautotest-inventory-no-file';
const EDIT = 'Edit';
const DOWNLOAD = 'Download';
const IMPACT = 'View impact';
const NO_FILE = 'No file';

function armCss(scope: string): string {
  return (
    compactCardsCss(scope) +
    cardPartCss('title', scope, 'line-height: 1.45;') +
    cardPartCss('body', scope, 'align-content: flex-start;') +
    chipsRowCss(scope, {
      nowrap: true,
      gap: '0 12px',
      lineHeight: '1.45',
      tabularNums: true,
      pill: false,
    }) +
    cardPartCss('chips', scope, 'flex: 1 0 100%;') +
    mediaCss(
      'phone',
      cardPartCss('chips', scope, 'flex-wrap: wrap; white-space: normal;'),
    ) +
    cardPartCss('thumbnail', scope, 'display: block !important; zoom: 0.36;') +
    panelActionCss(scope, { fontSize: '12px', marginTop: '0' }) +
    warningChipCss(scope)
  );
}

function marked(card: ProfileCard): boolean {
  return card.flag(FLAG) != null;
}

function linkedChip(
  card: ProfileCard,
  ctx: ArmContext,
  label: string,
  href: string,
  source: string,
): void {
  const chip = panelAction(label, href, () =>
    ctx.track(source, { entityId: card.workId }),
  );
  card.chipsRow()?.appendChild(chip);
}

function impactUrl(workId: number): string | null {
  if (!viewerIsPremium()) return upgradeUrl('analytics', IMPACT_SOURCE);

  const user = viewedUser();
  const pageName = user?.page_name;
  if (pageName == null || pageName === '') return null;

  return analyticsDocumentUrl(pageName, workId, user?.domain_name);
}

function statusChip(card: ProfileCard, ctx: ArmContext): void {
  if (card.hasDownloadable) return;

  const chip = warningChip(NO_FILE, workEditUrl(card.workId), () =>
    ctx.track(NO_FILE_SOURCE, { entityId: card.workId }),
  );
  card.chipsRow()?.appendChild(chip);
}

function compact(card: ProfileCard, ctx: ArmContext): void {
  card.setFlag(FLAG);
  card.collapse();
  linkedChip(card, ctx, EDIT, workEditUrl(card.workId), EDIT_SOURCE);
  if (card.downloadUrl != null) {
    linkedChip(card, ctx, DOWNLOAD, card.downloadUrl, DOWNLOAD_SOURCE);
  }
  const impact = impactUrl(card.workId);
  if (impact != null) linkedChip(card, ctx, IMPACT, impact, IMPACT_SOURCE);
  statusChip(card, ctx);
}

function countChip(
  card: ProfileCard,
  ctx: ArmContext,
  count: number | null,
  noun: string,
  feature: string,
  source: string,
): void {
  const label = countLabel(count, noun);
  if (label == null) return;

  const href = viewerIsPremium()
    ? card.url
    : upgradeUrl(feature, source, card.workId);
  if (href == null) {
    card.addChip(label);
    return;
  }

  linkedChip(card, ctx, label, href, source);
}

function tally(
  card: ProfileCard,
  ctx: ArmContext,
  views?: number | null,
  readers?: number,
  mentions?: number,
): void {
  const viewsChip = countLabel(positive(views), 'view');
  if (viewsChip != null) card.addChip(viewsChip);

  countChip(
    card,
    ctx,
    positive(readers),
    'bookmark',
    'readers',
    BOOKMARKS_SOURCE,
  );
  countChip(
    card,
    ctx,
    positive(mentions),
    'mention',
    'mentions',
    MENTIONS_SOURCE,
  );

  // The counts land after the status chip did, and addChip can only append.
  const row = card.chipsRow();
  const status = row == null ? null : warningChipIn(row);
  if (row != null && status != null) row.appendChild(status);
}

export const corpusInventory: ArmSpec = {
  onReady: async (ctx) => {
    const cards = ctx.cards();
    if (cards.length === 0) return;

    ctx.injectStyles(armCss(ctx.scope));
    cards.forEach((card) => compact(card, ctx));

    // One request per signal, covering the cards of the first render only.
    const workIds = cards.map((card) => card.workId);
    const [facts, readers, mentions] = await Promise.all([
      ctx.workFacts(),
      bookmarkCounts(workIds),
      mentionCounts(workIds),
    ]);

    cards.forEach((card) => {
      tally(
        card,
        ctx,
        facts.get(card.workId)?.views,
        readers.get(card.workId),
        mentions.get(card.workId),
      );
    });
  },

  // Collapsed and statused, never counted: a scroll batch is not worth a second
  // round trip. Gated on the marks onReady left, so a batch cannot engage an arm
  // that degraded to control.
  onCards: (cards, ctx) => {
    if (!ctx.cards().some(marked)) return;

    cards.filter((card) => !marked(card)).forEach((card) => compact(card, ctx));
  },
};
